import {AfterViewInit, Component, Input, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {TrackPlayerState, TrackPoint} from "../service";
import {TrackPlayerService} from "../service/track-player.service";
import {Subscription} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {DatePipe} from "@angular/common";
import * as L from "leaflet";
import {GlobalService, version} from "../../../global.config";
import {ModalController} from "@ionic/angular";
import {MapService} from "../service/map.service";
import {DataManagementService} from "../../../pages/data-management/data-management.service";

@Component({
  selector: 'app-track-controle',
  templateUrl: './track-controle.component.html',
  styleUrls: ['./track-controle.component.scss'],
})
export class TrackControleComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() trackData: any;
  @Input() trackType: string = "";
  version: String = version;

  stops: any;
  map: L.Map | null = null;
  playerState: TrackPlayerState = {
    isPlaying: false,
    isPaused: false,
    currentIndex: 0,
    totalPoints: 0,
    currentPosition: null,
    progress: 0,
    elapsedTime: 0,
    totalTime: 0
  };
  isControlsCollapsed = false;
  hasTrack = false;
  /** Zoom level used to follow the vehicle during playback. */
  private readonly playbackFollowZoom = 16;
  currentSpeed = 25;
  showTrail = true;
  showSpeedPoints = true; // New option for speed points
  showPartialPoints = false; // New option for partial points display
  loopPlayback = false;
  mapInitialized = false;

  // Distance tracking properties
  totalDistance = 0; // Total distance in km
  currentDistance = 0; // Current distance traveled in km
  private trackPoints: TrackPoint[] = [];
  /** Original GPS payload from the modal — never overwrite with TrackPoint[]. */
  private rawTrackData: any = null;
  /** Points waiting for the map to be ready. */
  private pendingTrackPoints: TrackPoint[] | null = null;

  // Vehicle max speed from service
  public vehicleMaxSpeed: number = 0;

  // Store stop markers for management
  private stopMarkers: L.Marker[] = [];
  private stopMarkersGroup: L.LayerGroup | null = null;

  // Store speed point markers
  private speedPointMarkers: L.CircleMarker[] = [];
  private speedPointsGroup: L.LayerGroup | null = null;

  // Speed point statistics
  speedPointStats = {
    total: 0,
    overSpeed: 0,
    normalSpeed: 0,
    displayed: 0
  };

  private currentMapLayerKey = 'OpenStreetMap';
  private trackBaseMaps: Record<string, L.TileLayer> | null = null;
  private subscriptions: Subscription[] = [];
  private suppressBaselayerSync = false;
  private mapSizeRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private mapSizeInitialized = false;

  constructor(
    private trackPlayerService: TrackPlayerService,
    private translateService: TranslateService,
    private globalService: GlobalService,
    private modalController: ModalController,
    private dataManagemenetService: DataManagementService,
    private mapService: MapService,
  ) {}

  ngOnInit(): void {
    const vehicle = this.dataManagemenetService.ensureSelectedVehicle();
    this.vehicleMaxSpeed = vehicle?.maxSpeed || 90;

    if (!this.trackData) {
      console.warn('No track data provided, closing modal');
      this.close();
      return;
    }

    this.rawTrackData = this.trackData;
    this.loadTrackFromGpsData();
    this.subscribeToPlayerState();
    this.subscribeToMapLayerChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.trackData && !changes.trackData.firstChange) {
      this.rawTrackData = this.trackData;
      this.loadTrackFromGpsData();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initializeMap();
    }, 100);
  }

  /** Ionic modal lifecycle — map size is reliable after the modal is fully shown. */
  ionViewDidEnter(): void {
    if (!this.mapSizeInitialized) {
      this.scheduleMapSizeRefresh();
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private initializeMap(): void {
    try {
      const mapContainer = document.getElementById('trackPlayerMap');
      if (!mapContainer) {
        console.error('Map container not found');
        return;
      }

      this.resetTrackMapContainer();

      this.trackBaseMaps = this.mapService.createFreshBaseMapLayers();
      const layerKey = this.mapService.resolveMapLayerKey();
      const defaultLayer = this.trackBaseMaps[layerKey];
      if (!defaultLayer) {
        console.error('Map layer not found:', layerKey);
        return;
      }
      this.currentMapLayerKey = layerKey;

      this.map = L.map('trackPlayerMap', {
        zoomControl: false,
        layers: [defaultLayer]
      }).setView([33.8869, 9.5375], 6);

      L.control.layers(this.trackBaseMaps).addTo(this.map);
      L.control.zoom({ position: 'topleft' }).addTo(this.map);

      this.map.on('baselayerchange', (e: any) => {
        if (this.suppressBaselayerSync) {
          return;
        }
        this.currentMapLayerKey = e.name;
        this.mapService.setSelectedMapLayer(e.name);
      });

      this.stopMarkersGroup = L.layerGroup().addTo(this.map);
      this.speedPointsGroup = L.layerGroup().addTo(this.map);
      this.trackPlayerService.initialize(this.map);
      this.mapInitialized = true;

      // Prefer pending transformed points; never rely on trackData.length
      // (raw GPS objects have no length, which left distances at 0 in prod).
      if (this.pendingTrackPoints?.length) {
        this.loadTrack(this.pendingTrackPoints);
      } else if (this.trackPoints?.length) {
        this.loadTrack(this.trackPoints);
      } else if (this.rawTrackData || this.trackData) {
        this.loadTrackFromGpsData();
      }

      this.ensureMapTilesVisible();
      // If the preferred layer fails (common with some providers), fall back to OSM once.
      let tileErrorHandled = false;
      defaultLayer.on('tileerror', () => {
        if (tileErrorHandled || this.currentMapLayerKey === 'OpenStreetMap') {
          return;
        }
        tileErrorHandled = true;
        this.applyTrackMapLayer('OpenStreetMap');
        this.scheduleMapSizeRefresh();
      });
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }

  /** Resize the modal map once after layout settles — avoid repeated tile redraw flashes. */
  private scheduleMapSizeRefresh(): void {
    if (this.mapSizeRefreshTimer) {
      clearTimeout(this.mapSizeRefreshTimer);
    }
    this.mapSizeRefreshTimer = setTimeout(() => {
      this.mapSizeRefreshTimer = null;
      if (!this.map) {
        return;
      }
      this.map.invalidateSize(true);
      this.mapSizeInitialized = true;
    }, 180);
  }

  private ensureMapTilesVisible(): void {
    this.scheduleMapSizeRefresh();
  }

  private resetTrackMapContainer(): void {
    const element = document.getElementById('trackPlayerMap');
    if (!element) {
      return;
    }

    if (this.map) {
      try {
        this.map.remove();
      } catch {
        // ignore
      }
      this.map = null;
    }

    element.innerHTML = '';
    delete (element as any)._leaflet_id;
  }

  private subscribeToMapLayerChanges(): void {
    const layerSubscription = this.mapService.mapLayerChange$.subscribe((layerKey) => {
      if (!this.map || !this.trackBaseMaps?.[layerKey] || layerKey === this.currentMapLayerKey) {
        return;
      }
      this.applyTrackMapLayer(layerKey);
    });
    this.subscriptions.push(layerSubscription);
  }

  private applyTrackMapLayer(layerKey: string): void {
    const newLayer = this.trackBaseMaps?.[layerKey];
    if (!this.map || !newLayer || layerKey === this.currentMapLayerKey) {
      return;
    }

    this.suppressBaselayerSync = true;
    this.map.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) {
        this.map!.removeLayer(layer);
      }
    });
    newLayer.addTo(this.map);
    this.currentMapLayerKey = layerKey;
    setTimeout(() => {
      this.suppressBaselayerSync = false;
    }, 0);
  }

  private subscribeToPlayerState(): void {
    const stateSubscription = this.trackPlayerService.state$.subscribe(state => {
      this.playerState = state;
      this.hasTrack = state.totalPoints > 0;
      // Update current distance when player state changes
      this.updateCurrentDistance();

      // Update partial points display if enabled
      if (this.showPartialPoints && this.showSpeedPoints) {
        this.updatePartialSpeedPoints();
      }
    });

    const positionSubscription = this.trackPlayerService.positionUpdate$.subscribe(position => {
      // Update current distance when position changes
      this.updateCurrentDistance();

      // Update partial points display if enabled
      if (this.showPartialPoints && this.showSpeedPoints) {
        this.updatePartialSpeedPoints();
      }
    });

    const endSubscription = this.trackPlayerService.playbackEnd$.subscribe(() => {
      // console.log('Playback ended');
    });

    this.subscriptions.push(stateSubscription, positionSubscription, endSubscription);
  }

  loadTrack(trackPoints: TrackPoint[]): void {
    if (!trackPoints?.length) {
      return;
    }

    const source = this.rawTrackData || this.trackData;
    if (source?.stops) {
      this.stops = source.stops;
    }

    // Distance does not need the map — compute immediately so UI is never stuck at 0m.
    this.trackPoints = trackPoints;
    this.calculateTotalDistance();
    this.calculateSpeedPointStats();

    if (!this.mapInitialized || !this.map) {
      this.pendingTrackPoints = trackPoints;
      return;
    }

    this.pendingTrackPoints = null;

    this.trackPlayerService.loadTrack(trackPoints, {
      speed: this.currentSpeed,
      showTrail: this.showTrail,
      trailColor: '#3388ff',
      trailWeight: 5,
      interpolate: true,
      loop: this.loopPlayback
    });

    if (this.trackType !== "path") {
      this.addStopMarkers();
    }

    // Add speed points if enabled
    if (this.showSpeedPoints) {
      this.addSpeedPointMarkers();
    }

    this.fitMapToBounds(trackPoints);
  }

  /**
   * Calculate speed point statistics
   */
  private calculateSpeedPointStats(): void {
    this.speedPointStats = {
      total: this.trackPoints.length,
      overSpeed: 0,
      normalSpeed: 0,
      displayed: 0
    };

    this.trackPoints.forEach(point => {
      if (point.speed > this.vehicleMaxSpeed) {
        this.speedPointStats.overSpeed++;
      } else {
        this.speedPointStats.normalSpeed++;
      }
    });
  }

  /**
   * Add speed point markers to the map
   */
  private addSpeedPointMarkers(): void {
    if (!this.map || !this.speedPointsGroup || this.trackPoints.length === 0) {
      return;
    }

    this.clearSpeedPointMarkers();

    const datePipe = new DatePipe('en-US');
    let displayedPoints = 0;

    this.trackPoints.forEach((point, index) => {
      // Skip if partial points is enabled and this point shouldn't be shown yet
      if (this.showPartialPoints && index > this.playerState.currentIndex) {
        return;
      }

      const isOverSpeed = point.speed > this.vehicleMaxSpeed;
      const color = isOverSpeed ? '#ff0000' : '#00ff00'; // Red for over speed, green for normal
      const fillColor = isOverSpeed ? '#ff0000' : '#00ff00';

      const marker = L.circleMarker([point.lat, point.lng], {
        radius: 4,
        fillColor: fillColor,
        color: color,
        weight: 2,
        opacity: 0.8,
        fillOpacity: 0.6
      });

      const formattedDate = datePipe.transform(point.timestamp, "dd/MM/yyyy HH:mm:ss", null, 'en-US');
      const speedStatus = isOverSpeed ? 'Over Speed Limit' : 'Normal Speed';
      const popup = `
        <div>
          <b>Time:</b> ${formattedDate}<br>
          <b>Speed:</b> ${point.speed.toFixed(1)} km/h<br>
          <b>Max Speed:</b> ${this.vehicleMaxSpeed} km/h<br>
          <b>Status:</b> <span style="color: ${color}; font-weight: bold;">${speedStatus}</span><br>
          <b>Position:</b> ${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}
        </div>
      `;

      marker.bindPopup(popup);

      marker.on('click', () => {
        if (this.map) {
          this.map.setView([point.lat, point.lng], 17);
        }
      });

      this.speedPointsGroup.addLayer(marker);
      this.speedPointMarkers.push(marker);
      displayedPoints++;
    });

    this.speedPointStats.displayed = displayedPoints;
    console.log(`Added ${displayedPoints} speed point markers`);
  }

  /**
   * Update partial speed points based on current playback position
   */
  private updatePartialSpeedPoints(): void {
    if (!this.showSpeedPoints || !this.showPartialPoints || !this.speedPointsGroup) {
      return;
    }

    // Clear and re-add markers up to current position
    this.addSpeedPointMarkers();
  }

  /**
   * Clear all speed point markers
   */
  private clearSpeedPointMarkers(): void {
    if (this.speedPointsGroup) {
      this.speedPointsGroup.clearLayers();
    }
    this.speedPointMarkers = [];
    this.speedPointStats.displayed = 0;
  }

  /**
   * Calculate total distance of the track
   */
  private calculateTotalDistance(): void {
    const fallbackDriven = Number(
      (this.rawTrackData || this.trackData)?.distanceDriven ?? 0
    );

    if (this.trackPoints.length < 2) {
      this.totalDistance = isFinite(fallbackDriven) && fallbackDriven > 0 ? fallbackDriven : 0;
      return;
    }

    let totalDistance = 0;
    for (let i = 0; i < this.trackPoints.length - 1; i++) {
      const point1 = this.trackPoints[i];
      const point2 = this.trackPoints[i + 1];
      const segment = this.calculateDistanceBetweenPoints(point1, point2);
      if (isFinite(segment)) {
        totalDistance += segment;
      }
    }

    if (!isFinite(totalDistance) || totalDistance <= 0) {
      this.totalDistance = isFinite(fallbackDriven) && fallbackDriven > 0 ? fallbackDriven : 0;
      return;
    }

    this.totalDistance = totalDistance;
  }

  /**
   * Update current distance based on player state
   */
  private updateCurrentDistance(): void {
    if (!this.trackPoints || this.trackPoints.length < 2 || this.playerState.currentIndex === 0) {
      this.currentDistance = 0;
      return;
    }

    let currentDistance = 0;
    const currentIndex = Math.min(this.playerState.currentIndex, this.trackPoints.length - 1);

    // Calculate distance up to current point
    for (let i = 0; i < currentIndex; i++) {
      const point1 = this.trackPoints[i];
      const point2 = this.trackPoints[i + 1];
      currentDistance += this.calculateDistanceBetweenPoints(point1, point2);
    }

    // If we have progress within the current segment, add partial distance
    if (currentIndex < this.trackPoints.length - 1) {
      const segmentProgress = this.playerState.progress / 100;
      const totalSegments = this.trackPoints.length - 1;
      const currentSegmentProgress = (segmentProgress * totalSegments) % 1;

      if (currentSegmentProgress > 0) {
        const point1 = this.trackPoints[currentIndex];
        const point2 = this.trackPoints[currentIndex + 1];
        const segmentDistance = this.calculateDistanceBetweenPoints(point1, point2);
        currentDistance += segmentDistance * currentSegmentProgress;
      }
    }

    this.currentDistance = currentDistance;
  }

  /**
   * Calculate distance between two points using Haversine formula
   */
  private calculateDistanceBetweenPoints(point1: TrackPoint, point2: TrackPoint): number {
    const R = 6371; // Earth's radius in km
    const dLat = this.deg2rad(point2.lat - point1.lat);
    const dLng = this.deg2rad(point2.lng - point1.lng);

    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.deg2rad(point1.lat)) * Math.cos(this.deg2rad(point2.lat)) *
      Math.sin(dLng/2) * Math.sin(dLng/2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  /**
   * Format distance for display
   */
  formatDistance(distance: number): string {
    if (!isFinite(distance) || distance < 0) {
      return '0m';
    }
    if (distance < 1) {
      return `${(distance * 1000).toFixed(0)}m`;
    }
    return `${distance.toFixed(2)}km`;
  }

  private fitMapToBounds(trackPoints: TrackPoint[]): void {
    if (!this.map || trackPoints.length === 0) return;

    const bounds = L.latLngBounds([]);

    trackPoints.forEach(point => {
      bounds.extend([point.lat, point.lng]);
    });

    if (this.trackType !== "path" && (this.rawTrackData || this.trackData)?.stops) {
      (this.rawTrackData || this.trackData).stops.forEach((stop: any) => {
        bounds.extend([stop.stopLatitude, stop.stopLongitude]);
      });
    }

    this.map.fitBounds(bounds, { padding: [50, 50], animate: false, maxZoom: 17 });
  }

  private addStopMarkers(): void {
    if (!this.stops || !this.map || !this.stopMarkersGroup) {
      return;
    }

    this.clearStopMarkers();

    const datePipe = new DatePipe('en-US');

    this.stops.forEach((stop: any, index: number) => {
      const marker = L.marker([stop.stopLatitude, stop.stopLongitude]);

      const formattedDate = datePipe.transform(stop.beginStopTime, "dd/MM/yyyy HH:mm:ss", null, 'en-US');
      const popup = `
        <div>
          <b>Date début d'arrêt:</b> ${formattedDate}<br>
          <b>Durée d'arrêt:</b> ${stop.stopDurationStr}
        </div>
      `;

      const stopIcon = L.icon({
        iconUrl: this.globalService.imagesDir + "stop_smal.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34]
      });

      marker.setIcon(stopIcon);
      marker.bindPopup(popup);

      marker.on('click', () => {
        if (this.map) {
          this.map.setView([stop.stopLatitude, stop.stopLongitude], 17);
        }
      });

      this.stopMarkersGroup.addLayer(marker);
      this.stopMarkers.push(marker);
    });
  }

  private clearStopMarkers(): void {
    if (this.stopMarkersGroup) {
      this.stopMarkersGroup.clearLayers();
    }
    this.stopMarkers = [];
  }

  togglePlayback(): void {
    if (this.playerState.isPlaying) {
      this.trackPlayerService.pause();
    } else if (this.playerState.isPaused) {
      this.trackPlayerService.resume();
    } else {
      this.startPlaybackZoomedOnVehicle();
    }
  }

  /**
   * Zoom the map in on the vehicle's start position, then start playback.
   * Playback must wait for the zoom move to finish, otherwise the
   * follow-camera (setView on the vehicle) cancels the zoom immediately.
   */
  private startPlaybackZoomedOnVehicle(): void {
    if (!this.map || this.trackPoints.length === 0) {
      this.trackPlayerService.play();
      return;
    }

    let started = false;
    const startOnce = () => {
      if (started) {
        return;
      }
      started = true;
      this.map?.off('moveend', startOnce);
      this.trackPlayerService.play();
    };

    const start = this.trackPoints[0];
    this.map.once('moveend', startOnce);
    this.map.setView([start.lat, start.lng], this.playbackFollowZoom, { animate: true });
    // Fallback: if the map was already at the right view, no moveend fires.
    setTimeout(startOnce, 1000);
  }

  stop(): void {
    this.trackPlayerService.stop();
  }

  onSeek(event: Event): void {
    const target = event.target as HTMLInputElement;
    const percentage = parseFloat(target.value);
    this.trackPlayerService.seekTo(percentage);
  }

  onSpeedChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.currentSpeed = parseFloat(target.value);
    this.trackPlayerService.setSpeed(this.currentSpeed);
  }

  /**
   * Set speed directly to a specific value
   */
  setSpeed(speed: number): void {
    this.currentSpeed = speed;
    this.trackPlayerService.setSpeed(this.currentSpeed);
  }

  onShowTrailChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.showTrail = target.checked;
    this.trackPlayerService.updateOptions({ showTrail: this.showTrail });
  }

  /**
   * Handle speed points visibility toggle
   */
  onShowSpeedPointsChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.showSpeedPoints = target.checked;

    if (this.showSpeedPoints) {
      this.addSpeedPointMarkers();
    } else {
      this.clearSpeedPointMarkers();
    }
  }

  /**
   * Handle partial points display toggle
   */
  onShowPartialPointsChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.showPartialPoints = target.checked;

    // Update the display if speed points are enabled
    if (this.showSpeedPoints) {
      this.addSpeedPointMarkers();
    }
  }

  onLoopChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.loopPlayback = target.checked;
    this.trackPlayerService.updateOptions({ loop: this.loopPlayback });
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  transformGpsDataToTrackPoints(): TrackPoint[] {
    const data = this.rawTrackData || this.trackData;
    let trackPoints: TrackPoint[] = [];

    if (this.trackType !== "mileage") {
      if (!data || !data.coordinates || !Array.isArray(data.coordinates)) {
        console.error('Invalid GPS data provided');
        return [];
      }
      trackPoints = data.coordinates
        .map((coord: any, index: number) => this.mapCoordToTrackPoint(coord, data.coordinates[index + 1], index))
        .filter((p: TrackPoint | null): p is TrackPoint => !!p);
    } else {
      if (!data || !data.points || !Array.isArray(data.points)) {
        console.error('Invalid GPS data provided');
        return [];
      }
      trackPoints = data.points
        .map((coord: any, index: number) => this.mapCoordToTrackPoint(coord, data.points[index + 1], index))
        .filter((p: TrackPoint | null): p is TrackPoint => !!p);
    }

    return trackPoints;
  }

  private mapCoordToTrackPoint(coord: any, next: any, index: number): TrackPoint | null {
    const lat = this.readCoordNumber(coord, ['lat', 'latitude', 'y']);
    const lng = this.readCoordNumber(coord, ['lng', 'lon', 'longitude', 'x']);
    if (lat == null || lng == null) {
      return null;
    }
    return {
      lat,
      lng,
      timestamp: coord.date ?? coord.timestamp,
      speed: coord.speed,
      heading: this.calculateHeading({ lat, lng }, next ? {
        lat: this.readCoordNumber(next, ['lat', 'latitude', 'y']),
        lng: this.readCoordNumber(next, ['lng', 'lon', 'longitude', 'x']),
      } : null),
      altitude: coord.altitude || 0,
      memsX: coord.memsX,
      memsY: coord.memsY,
      memsZ: coord.memsZ,
      aboveSpeedLimit: coord.aboveTheSpeedLimit,
      originalIndex: index
    };
  }

  private readCoordNumber(coord: any, keys: string[]): number | null {
    if (!coord) {
      return null;
    }
    for (const key of keys) {
      const value = Number(coord[key]);
      if (isFinite(value)) {
        return value;
      }
    }
    return null;
  }

  private calculateHeading(point1: any, point2: any): number {
    if (!point2 || point1?.lat == null || point1?.lng == null || point2?.lat == null || point2?.lng == null) {
      return 0;
    }

    const lat1Rad = this.deg2rad(point1.lat);
    const lat2Rad = this.deg2rad(point2.lat);
    const deltaLngRad = this.deg2rad(point2.lng - point1.lng);

    const y = Math.sin(deltaLngRad) * Math.cos(lat2Rad);
    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
      Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(deltaLngRad);

    const headingRad = Math.atan2(y, x);
    const headingDeg = this.rad2deg(headingRad);

    return (headingDeg + 360) % 360;
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  private rad2deg(rad: number): number {
    return rad * (180 / Math.PI);
  }

  loadTrackFromGpsData(): void {
    const trackPoints = this.transformGpsDataToTrackPoints();
    if (trackPoints.length > 0) {
      this.loadTrack(trackPoints);
    } else {
      console.warn('No valid track points found in GPS data');
    }
  }

  getTrackStatistics(gpsData: any): any {
    if (!gpsData) return null;

    return {
      deviceId: gpsData.deviceId,
      startTime: new Date(gpsData.beginPathTime),
      endTime: new Date(gpsData.endPathTime),
      duration: gpsData.pathDuration,
      durationFormatted: this.formatTime(gpsData.pathDuration),
      distance: gpsData.distanceDriven,
      distanceFormatted: (gpsData.distanceDriven * 1000).toFixed(0) + 'm',
      maxSpeed: gpsData.maxSpeed,
      avgSpeed: gpsData.pathDuration > 0 ?
        ((gpsData.distanceDriven / (gpsData.pathDuration / 3600)).toFixed(1)) : 0,
      pointCount: gpsData.coordinates ? gpsData.coordinates.length : 0,
      startLocation: {
        lat: gpsData.beginPathLatitude,
        lng: gpsData.beginPathLongitude
      },
      endLocation: {
        lat: gpsData.endPathLatitude,
        lng: gpsData.endPathLongitude
      },
      fuelUsed: gpsData.fuelUsed,
      odometer: {
        start: gpsData.startOdo,
        end: gpsData.endOdo,
        distance: gpsData.endOdo - gpsData.startOdo
      }
    };
  }

  resizeMap(): void {
    this.ensureMapTilesVisible();
  }

  getPlayButtonText(): string {
    if (this.playerState.isPlaying) {
      return this.translateService.instant('TRACK_PLAYER.pause');
    } else if (this.playerState.isPaused) {
      return this.translateService.instant('TRACK_PLAYER.resume');
    } else {
      return this.translateService.instant('TRACK_PLAYER.play');
    }
  }

  private cleanup(): void {
    if (this.mapSizeRefreshTimer) {
      clearTimeout(this.mapSizeRefreshTimer);
      this.mapSizeRefreshTimer = null;
    }
    this.mapSizeInitialized = false;
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    this.trackPlayerService.stop();
    this.trackPlayerService.clearTrack();
    this.clearStopMarkers();
    this.clearSpeedPointMarkers();
    this.resetTrackMapContainer();
    this.trackBaseMaps = null;
    this.mapInitialized = false;
    this.playerState = {
      isPlaying: false,
      isPaused: false,
      currentIndex: 0,
      totalPoints: 0,
      currentPosition: null,
      progress: 0,
      elapsedTime: 0,
      totalTime: 0
    };
  }

  close(): void {
    this.cleanup(); // manually cleanup resources
    this.modalController.dismiss();
  }

  hasSelectedVehicle(): boolean {
    return !!this.getSelectedVehicleLabel();
  }

  getSelectedVehicleLabel(): string {
    const vehicule = this.dataManagemenetService.selectedVehicule;
    if (!vehicule) {
      return '';
    }
    return this.dataManagemenetService.getVehiculeName(vehicule) || vehicule.matricule || '';
  }


  /**
   * Toggle the collapse/expand state of the controls panel
   */
  toggleControls(): void {
    this.isControlsCollapsed = !this.isControlsCollapsed;

    // Resize map after the CSS transition completes
    setTimeout(() => {
      this.resizeMap();
    }, 350);
  }
}
